import { z } from 'zod'
import { type AnalyticsEventPayload } from '#app/utils/pz/analytics.catalog'

/**
 * Server-confirmed events travel to the next page in short-lived cookies the
 * browser reads. Each event has its own cookie, `analytics_event_<id>`, so two
 * responses never overwrite each other's events. The contents are validated
 * on both sides: only these events, with their catalog properties, ever reach
 * the data layer.
 */
export const analyticsFlashCookiePrefix = 'analytics_event_'

/** Long enough for a visitor to accept analytics later in the same visit. */
export const analyticsFlashMaxAgeSeconds = 30 * 60

const flashId = z.string().regex(/^[A-Za-z0-9-]{8,64}$/)
const source = z
	.string()
	.regex(/^[a-z0-9_-]{1,64}$/)
	.optional()

const analyticsFlashEventSchema = z.discriminatedUnion('name', [
	z
		.object({
			id: flashId,
			name: z.literal('signup_verification_sent'),
			properties: z
				.object({ method: z.literal('email').optional(), source })
				.strict()
				.optional(),
		})
		.strict(),
	z
		.object({
			id: flashId,
			name: z.literal('signup_completed'),
			properties: z
				.object({ method: z.enum(['email', 'oauth']).optional(), source })
				.strict()
				.optional(),
		})
		.strict(),
	z
		.object({
			id: flashId,
			name: z.literal('onboarding_completed'),
			properties: z.object({}).strict().optional(),
		})
		.strict(),
	z
		.object({
			id: flashId,
			name: z.literal('login_completed'),
			properties: z
				.object({ method: z.enum(['email', 'oauth', 'credentials']) })
				.strict(),
		})
		.strict(),
])

export type AnalyticsFlashEvent = z.infer<typeof analyticsFlashEventSchema>

export function isAnalyticsFlashCookieName(name: string): boolean {
	return name.startsWith(analyticsFlashCookiePrefix)
}

/**
 * Parses one cookie value into valid flash events. Anything malformed or
 * outside the allowlist is dropped, never emitted.
 */
export function parseAnalyticsFlashEvents(
	rawValue: string | null | undefined,
): AnalyticsFlashEvent[] {
	if (!rawValue) return []
	let parsed: unknown
	try {
		parsed = JSON.parse(decodeURIComponent(rawValue))
	} catch {
		return []
	}
	const candidates = Array.isArray(parsed) ? parsed : [parsed]
	return candidates.flatMap((candidate) => {
		const result = analyticsFlashEventSchema.safeParse(candidate)
		return result.success ? [result.data] : []
	})
}

export function toAnalyticsEventPayload(
	event: AnalyticsFlashEvent,
): AnalyticsEventPayload {
	return {
		name: event.name,
		properties: event.properties ?? {},
	} as AnalyticsEventPayload
}
